import { createBrowserRouter, Navigate } from 'react-router-dom';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { PublicRoute } from './components/auth/PublicRoute';

// Auth pages (lazy loaded)
import { LoginPage } from './features/auth/LoginPage';
import { RegisterPage } from './features/auth/RegisterPage';
import { ForgotPasswordPage } from './features/auth/ForgotPasswordPage';
import { ResetPasswordPage } from './features/auth/ResetPasswordPage';
import { AcceptInvitePage } from './features/users/AcceptInvitePage';
import { NoOrganizationPage } from './features/onboarding/NoOrganizationPage';
import { PricingPage } from './features/pricing/PricingPage';

// Protected pages (lazy loaded)
import { DashboardPage } from './features/dashboard/DashboardPage';
import { AssetsPage } from './features/assets/AssetsPage';
import { ScanQRPage } from './features/assets/ScanQRPage';
import { AssetTokenPage } from './features/assets/AssetTokenPage';
import { PeoplePage } from './features/people/PeoplePage';
import { CategoriesPage } from './features/categories/CategoriesPage';
import { DepartmentsPage } from './features/departments/DepartmentsPage';
import { LocationsPage } from './features/locations/LocationsPage';
import { CustomFieldsPage } from './features/custom-fields/CustomFieldsPage';
import { SettingsPage } from './features/settings/SettingsPage';
import { AssetFieldsPage } from './features/settings/AssetFieldsPage';
import { UsersPage } from './features/users/UsersPage';
import { RolesPage } from './features/roles/RolesPage';
import { CustomReportsPage } from './features/reports/CustomReportsPage';
import { CreateReportPage } from './features/reports/CreateReportPage';
import { ReportAutomationsPage } from './features/reports/ReportAutomationsPage';
import { ReportPresetsPage } from './features/reports/ReportPresetsPage';
import { AuditsPage } from './features/audits/AuditsPage';
import { AuditDetailPage } from './features/audits/AuditDetailPage';
import { AuditReviewPage } from './features/audits/AuditReviewPage';
import { ActivityLogPage } from './features/activity-log/ActivityLogPage';
import { ProfilePage } from './features/profile/ProfilePage';
import { HelpPage } from './features/help/HelpPage';
import { NotificationsPage } from './features/notifications/NotificationsPage';

// Layout
import { AppLayout } from './components/layout/AppLayout';

export const router = createBrowserRouter([
  {
    path: '/',
    element: <Navigate to="/dashboard" replace />,
  },
  {
    // Public routes (redirect to dashboard if authenticated)
    element: <PublicRoute />,
    children: [
      {
        path: '/login',
        element: <LoginPage />,
      },
      {
        path: '/pricing',
        element: <PricingPage />,
      },
      {
        path: '/register',
        element: <RegisterPage />,
      },
      {
        path: '/forgot-password',
        element: <ForgotPasswordPage />,
      },
      {
        path: '/reset-password',
        element: <ResetPasswordPage />,
      },
    ],
  },
  {
    // Accept invite route (accessible to both logged in and logged out users)
    path: '/accept-invite',
    element: <AcceptInvitePage />,
  },
  {
    // Asset token route - accessible to anyone (redirects to login if not authenticated)
    // This allows QR codes to work from any QR scanner app
    path: '/a/:token',
    element: <AssetTokenPage />,
  },
  {
    // Get started page - for authenticated users without an organization
    // Uses ProtectedRoute without requireOrganization
    element: <ProtectedRoute />,
    children: [
      {
        path: '/get-started',
        element: <NoOrganizationPage />,
      },
    ],
  },
  {
    // Protected routes (require authentication AND organization)
    element: <ProtectedRoute requireOrganization />,
    children: [
      {
        element: <AppLayout />,
        children: [
          {
            path: '/dashboard',
            element: <DashboardPage />,
          },
          {
            path: '/assets',
            element: <AssetsPage />,
          },
          {
            path: '/assets/scan',
            element: <ScanQRPage />,
          },
          {
            path: '/settings/people',
            element: <PeoplePage />,
          },
          {
            path: '/settings/locations',
            element: <LocationsPage />,
          },
          {
            path: '/settings/categories',
            element: <CategoriesPage />,
          },
          {
            path: '/settings/departments',
            element: <DepartmentsPage />,
          },
          {
            path: '/settings/fields',
            element: <AssetFieldsPage />,
          },
          {
            path: '/settings/custom-fields',
            element: <CustomFieldsPage />,
          },
          {
            path: '/settings',
            element: <SettingsPage />,
          },
          {
            path: '/settings/users',
            element: <UsersPage />,
          },
          {
            path: '/settings/roles',
            element: <RolesPage />,
          },
          {
            path: '/reports',
            element: <Navigate to="/reports/presets" replace />,
          },
          {
            path: '/reports/presets',
            element: <ReportPresetsPage />,
          },
          {
            path: '/reports/saved',
            element: <CustomReportsPage />,
          },
          {
            path: '/reports/create',
            element: <CreateReportPage />,
          },
          {
            path: '/reports/edit/:reportId',
            element: <CreateReportPage />,
          },
          {
            path: '/reports/automations',
            element: <ReportAutomationsPage />,
          },
          {
            path: '/audits',
            element: <AuditsPage />,
          },
          {
            path: '/audits/:auditId',
            element: <AuditDetailPage />,
          },
          {
            path: '/audits/:auditId/review',
            element: <AuditReviewPage />,
          },
          {
            path: '/activity-log',
            element: <ActivityLogPage />,
          },
          {
            path: '/profile',
            element: <ProfilePage />,
          },
          {
            path: '/notifications',
            element: <NotificationsPage />,
          },
          {
            path: '/help',
            element: <HelpPage />,
          },
        ],
      },
    ],
  },
  {
    path: '*',
    element: <Navigate to="/dashboard" replace />,
  },
]);
